import api from './BaseApi';
import { handleApiError } from './handleApiError';
import { ResponseBody } from '../types/ResponseBody';

export const chunkArray = <T>(items: T[], size: number): T[][] => {
  if (size <= 0) {
    throw new Error('chunkArray size must be greater than 0');
  }
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

export type TChunkedGetOutcome<TResponse> = {
  chunk: string[];
  result: PromiseSettledResult<ResponseBody<TResponse>>;
};

// GETs an endpoint that accepts a repeated query param (e.g. ?ids=a&ids=b), splitting
// `values` into chunks so the query string stays well under URL length limits, firing
// each chunk in parallel. One chunk failing doesn't block the others - callers get the
// settled outcome per chunk (and which values were in it) to merge however fits their
// response shape (e.g. per-item error map vs. a plain list).
export const getChunked = async <TResponse>(
  path: string,
  paramKey: string,
  values: string[],
  chunkSize = 30
): Promise<TChunkedGetOutcome<TResponse>[]> => {
  const chunks = chunkArray(values, chunkSize);

  const settled = await Promise.allSettled(
    chunks.map((chunk) => {
      const urlParams = new URLSearchParams();
      chunk.forEach((value) => urlParams.append(paramKey, value));
      return api.get<TResponse>(`${path}?${urlParams.toString()}`).catch(handleApiError);
    })
  );

  return chunks.map((chunk, index) => ({ chunk, result: settled[index] }));
};

export type TBatchByIdResponse<TItem> = {
  success: boolean;
  results: Record<string, TItem>;
};

// For the common backend pattern of a chunked-GET returning { success, results: Record<id, TItem> }
// (e.g. RsBatchWalletBalances). Merges every chunk's `results` into one map; if a chunk's request
// fails outright, every id in that chunk gets `fallbackForFailedChunk(id)` instead of being dropped.
export const getChunkedBatchById = async <TItem>(
  path: string,
  paramKey: string,
  values: string[],
  fallbackForFailedChunk: (id: string) => TItem,
  chunkSize = 30
): Promise<ResponseBody<TBatchByIdResponse<TItem>>> => {
  if (values.length === 0) {
    return { success: true, data: { success: true, results: {} } } as ResponseBody<
      TBatchByIdResponse<TItem>
    >;
  }

  const outcomes = await getChunked<TBatchByIdResponse<TItem>>(path, paramKey, values, chunkSize);

  const results: Record<string, TItem> = {};

  outcomes.forEach(({ chunk, result }) => {
    if (result.status === 'fulfilled') {
      Object.assign(results, result.value.data.results);
    } else {
      console.error(`Failed to fetch a chunk from ${path}:`, result.reason);
      chunk.forEach((id) => {
        results[id] = fallbackForFailedChunk(id);
      });
    }
  });

  const success = outcomes.every(
    ({ result }) => result.status === 'fulfilled' && result.value.data.success
  );

  // Use a real chunk's envelope (status/code, and any other fields the backend sends)
  // as the template rather than inventing one, then swap in the merged data/success.
  // Only falls back to a synthetic envelope if every single chunk failed outright.
  const firstFulfilled = outcomes
    .map(({ result }) => result)
    .find(
      (result): result is PromiseFulfilledResult<ResponseBody<TBatchByIdResponse<TItem>>> =>
        result.status === 'fulfilled'
    );
  const envelope = firstFulfilled?.value ?? { status: 'error', code: 'unknown' };

  return { ...envelope, success, data: { success, results } };
};
